import { Injectable } from '@angular/core';

export interface SmartCltSuggestion {
  recommendFolga: boolean;
  entrada: string;
  saidaIntervalo: string;
  retornoIntervalo: string;
  saida: string;
  summaryLabel: string;
}

@Injectable({
  providedIn: 'root',
})
export class CltValidationRulesService {
  toMin(timeStr?: string): number | null {
    if (!timeStr || !timeStr.includes(':')) return null;
    const [hStr, mStr] = timeStr.split(':');
    const h = Number(hStr);
    const m = Number(mStr);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  }

  fromMin(mins: number): string {
    const norm = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(norm / 60);
    const m = norm % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  calculateInterShiftRestMinutes(
    prevEntMin: number | null,
    prevSaiMin: number,
    currEntMin: number
  ): number {
    const crossedMidnight =
      (prevEntMin !== null && prevSaiMin <= prevEntMin) ||
      (prevEntMin === null && prevSaiMin <= 6 * 60);
    if (crossedMidnight) {
      return currEntMin - prevSaiMin;
    }
    return 24 * 60 - prevSaiMin + currEntMin;
  }

  validateShiftClt(
    entrada: string,
    saidaIntervalo: string,
    retornoIntervalo: string,
    saida: string,
    previousDaySaida?: string,
    consecutiveDaysWorked = 5,
    workedLastThreeSundays = false,
    previousDayEntrada?: string,
    ruleConfig?: {
      maxHorasSemIntervalo?: number;
      intervaloMinHoras?: number;
      intervaloMaxHoras?: number;
    }
  ): string[] {
    const alerts: string[] = [];
    const entMin = this.toMin(entrada);
    const sIntMin = this.toMin(saidaIntervalo);
    const rIntMin = this.toMin(retornoIntervalo);
    let saiMin = this.toMin(saida);

    if (entMin !== null && saiMin !== null) {
      if (saiMin <= entMin) saiMin += 24 * 60;

      let breakDuration = 0;
      let firstPartMin = 0;
      let secondPartMin = 0;
      if (sIntMin !== null && rIntMin !== null) {
        const effSInt = sIntMin < entMin ? sIntMin + 1440 : sIntMin;
        const effRInt = rIntMin <= effSInt ? rIntMin + 1440 : rIntMin;
        if (effSInt >= entMin && effRInt > effSInt && effRInt <= saiMin) {
          firstPartMin = effSInt - entMin;
          breakDuration = effRInt - effSInt;
          secondPartMin = Math.max(0, saiMin - effRInt);
        }
      }

      if (previousDaySaida && previousDaySaida.includes(':')) {
        const prevSai = this.toMin(previousDaySaida);
        const prevEnt = previousDayEntrada ? this.toMin(previousDayEntrada) : null;
        if (prevSai !== null) {
          const restMinutes = this.calculateInterShiftRestMinutes(prevEnt, prevSai, entMin);
          if (restMinutes < 11 * 60) {
            const restH = (restMinutes / 60).toFixed(1);
            const minEntryStr = this.fromMin(prevSai + 11 * 60);
            alerts.push(
              `O colaborador saiu às ${previousDaySaida} no turno anterior (${restH}h de descanso interjornada). Para respeitar as 11h mínimas de descanso da CLT (Art. 66), o turno de hoje deve iniciar a partir das ${minEntryStr}.`
            );
          }
        }
      }

      const minBreakMin = Math.round((ruleConfig?.intervaloMinHoras ?? 1) * 60);
      const maxBreakMin = Math.round((ruleConfig?.intervaloMaxHoras ?? 4) * 60);
      const netWorkMin = saiMin - entMin - breakDuration;
      if (netWorkMin > 360 && breakDuration < minBreakMin) {
        alerts.push(
          `Intervalo intrajornada insuficiente (${breakDuration} min). Pela CLT (Art. 71), toda jornada acima de 6h exige no mínimo ${ruleConfig?.intervaloMinHoras ?? 1}h (${minBreakMin} min) de pausa para refeição e descanso.`
        );
      }

      const maxPartMin = Math.round((ruleConfig?.maxHorasSemIntervalo ?? 6) * 60);
      if (firstPartMin > maxPartMin || secondPartMin > maxPartMin) {
        alerts.push(
          `Período contínuo superior a ${ruleConfig?.maxHorasSemIntervalo ?? 6}h seguidas sem pausa (1ª ou 2ª parte do turno). Posicione o intervalo no meio da jornada para equilibrar o ritmo operacional.`
        );
      }

      if (breakDuration > maxBreakMin) {
        alerts.push(
          `O intervalo intrajornada (${(breakDuration / 60).toFixed(1)}h) excede o limite máximo parametrizado de ${ruleConfig?.intervaloMaxHoras ?? 4}h (Art. 71 CLT).`
        );
      }

      if (netWorkMin > 600) {
        alerts.push(
          `A jornada líquida diária (${(netWorkMin / 60).toFixed(1)}h) ultrapassa o teto legal de 10h (máximo de 2h extras pelo Art. 59 da CLT).`
        );
      }
    }

    if (consecutiveDaysWorked > 6) {
      alerts.push(
        `Este seria o ${consecutiveDaysWorked}º dia consecutivo trabalhado. Para respeitar o Descanso Semanal Remunerado (Art. 67 CLT / OJ 410 TST - máx. 6 dias seguidos), conceda FOLGA hoje.`
      );
    }

    if (workedLastThreeSundays) {
      alerts.push(
        'O colaborador atingiu o limite de domingos consecutivos sem folga (Lei 10.101/2000 & Art. 386 CLT). Conceda FOLGA neste domingo.'
      );
    }

    return alerts;
  }

  computeSmartCltSuggestion(
    entrada: string,
    previousDaySaida?: string,
    consecutiveDaysWorked = 5,
    workedLastThreeSundays = false,
    previousDayEntrada?: string,
    workDurationMin = 440,
    ruleConfig?: {
      maxHorasSemIntervalo?: number;
      intervaloMinHoras?: number;
      intervaloMaxHoras?: number;
    }
  ): SmartCltSuggestion {
    let targetEntMin = this.toMin(entrada) ?? 9 * 60 + 30;
    if (previousDaySaida && previousDaySaida.includes(':')) {
      const prevSai = this.toMin(previousDaySaida);
      const prevEnt = previousDayEntrada ? this.toMin(previousDayEntrada) : null;
      if (prevSai !== null) {
        const restMinutes = this.calculateInterShiftRestMinutes(prevEnt, prevSai, targetEntMin);
        if (restMinutes < 11 * 60) {
          targetEntMin = ((prevSai + 11 * 60) % (24 * 60) + 24 * 60) % (24 * 60);
        }
      }
    }

    const workMin = workDurationMin > 0 ? workDurationMin : 440;
    const minBreakMin = Math.round((ruleConfig?.intervaloMinHoras ?? 1) * 60);
    const breakMin = workMin > 360 ? minBreakMin : workMin > 240 ? 15 : 0;
    const maxSinglePartMin = Math.round((ruleConfig?.maxHorasSemIntervalo ?? 6) * 60);
    const firstPartMin = breakMin > 0 ? Math.min(maxSinglePartMin, Math.round(workMin / 2)) : workMin;
    const secondPartMin = breakMin > 0 ? Math.max(0, workMin - firstPartMin) : 0;

    const sIntMin = targetEntMin + firstPartMin;
    const rIntMin = sIntMin + breakMin;
    const saiMin = rIntMin + secondPartMin;

    const entStr = this.fromMin(targetEntMin);
    const sIntStr = breakMin > 0 ? this.fromMin(sIntMin) : '-';
    const rIntStr = breakMin > 0 ? this.fromMin(rIntMin) : '-';
    const saiStr = this.fromMin(saiMin);

    const onlyCycleViolation =
      (consecutiveDaysWorked > 6 || workedLastThreeSundays) &&
      targetEntMin === (this.toMin(entrada) ?? 9 * 60 + 30);

    const workH = String(Math.floor(workMin / 60)).padStart(2, '0');
    const workM = String(workMin % 60).padStart(2, '0');
    const breakLabel =
      breakMin >= 60
        ? `${Math.floor(breakMin / 60)}h${breakMin % 60 > 0 ? String(breakMin % 60).padStart(2, '0') : '00'}`
        : `${breakMin}m`;

    return {
      recommendFolga: onlyCycleViolation,
      entrada: entStr,
      saidaIntervalo: sIntStr,
      retornoIntervalo: rIntStr,
      saida: saiStr,
      summaryLabel: onlyCycleViolation
        ? 'Converter turno de hoje em FOLGA (DSR) para zerar o ciclo consecutivo/dominical'
        : breakMin > 0
          ? `Entrada ${entStr} · Intervalo ${sIntStr} a ${rIntStr} (${breakLabel}) · Saída ${saiStr} (Jornada ${workH}h${workM})`
          : `Entrada ${entStr} · Sem intervalo obrigatório · Saída ${saiStr} (Jornada ${workH}h${workM})`,
    };
  }
}
